import CameraService, { CameraPermissionState, CameraFacingMode } from '../../../helpers/classes/CameraService';
import EventService from '../../../helpers/classes/EventService';
import { ImageService } from '../../../helpers/classes/ImageService';
import LanguageService from '../../../helpers/classes/LanguageService';
import RenderingService from '../../../helpers/classes/RenderingService';
import TemplateService from '../../../helpers/classes/TemplateService';
import { retryOperationWithTimeout, wait } from '../../../helpers/functions/functions';
import IdesMicroHost from '../../../ides/host';
import { IImageResult, IImageCounter } from '../../../ides/interfaces/image';
import { JourneyContainer } from '../../classes/JourneyContainer';
import { HintType, ReticleType } from '../../enums/Camera';
import { InputProvider } from '../../enums/InputProvider';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { Templates } from '../../enums/Templates';
import { TranslationKey } from '../../enums/Translation';
import { ImageProvider } from '../provider/image';
import { IMetaData } from '../../interfaces/MetaData';
import * as lottie from 'lottie-web/build/player/lottie';
import { FlowState } from '../../enums/FlowState';
import CancellationToken from 'cancellationtoken';
import { SmartCaptureFailingReasons } from '../../../helpers/enums/smartCaptureFailingReasons';
import { RequiredInputImage } from '../../enums/RequiredInputImage';
import DataService, { DataStoreType } from '../../classes/DataService';
import { IIdScanData } from '../../interfaces/DataFormats';
import { AnimationService } from '../../../helpers/classes/AnimationService';
import { TripleScanGuidanceProvider } from './triple-scan-guidance';

declare var ENV: string;
export class SmartCaptureProvider {

	public static render(this: JourneyContainer, isFromTripleScanGuidancePage: boolean = false, isPredefinedJourney: boolean = false) {
		const { recaptureDelay = 5, workerScriptUrl = './ides-micro.js', asmScriptUrl = './idesmicro_asm.js', timeout = 60000, timeLogsEnabled = true, autoStart = false } = this._config.smartCapture || {};

		return TemplateService.getElements(this._container, {
			providerContainer: '[data-jcs-element="provider__container"]',
		})
			.then(({ providerContainer }) => TemplateService.renderToContainer(providerContainer, Templates.SmartCapture))
			.then(() => TemplateService.getElements(this._container, {
				cameraStatus: '[data-jcs-element="camera__status"]',
				cameraCapture: '[data-jcs-element="camera__capture"]',
				cameraViewfinder: '[data-jcs-element="camera__viewfinder"]',
				cameraViewfinderInnerContainer: '[data-jcs-element="camera__viewfinder__inner__container"]',
				cameraSelect: '[data-jcs-element="camera__select"]',
				errorImg: '[data-jcs-element="canvas__condition__error"]',
				alignment: '[data-jcs-element="camera__condition__alignment"]',
				blur: '[data-jcs-element="camera__condition__blur"]',
				uploading: '[data-jcs-element="camera__condition__uploading"]',
				glare: '[data-jcs-element="camera__condition__glare"]',
				lowResolution: '[data-jcs-element="camera__condition__low__resolution"]',
				actionContainer: '[data-jcs-element="info__journey__action__container"]',
				actionText: '[data-jcs-element="info__journey__action__text"]',
				hintAnimationContainer: '[data-jcs-element="animation__viewfinder"]',
				cameraOverlay: '[data-jcs-element="overlay_viewfinder"]',
				loadingAnimationContainer: '[data-jcs-element="loading-animation"]',
				verifyingAnimation: '[data-jcs-element="verifying__title"]',
				manualCaptureButton: '[data-jcs-element="camera__manual__capture"]',
				modalManualCaptureButton: '[data-jcs-element="capture__modal__camera__manual__capture"]',
				modalManualCaptureText: '[data-jcs-element="capture__modal__manual__capture__text"]',
				manualCaptureCheck: '[data-jcs-element="camera__manual__capture__check"]',
				modalManualCaptureCheck: '[data-jcs-element="capture__modal__camera__manual__capture__check"]',
				closeHelp: '[data-jcs-element="smart__capture__close__help"]',
				openHelp: '[data-jcs-element="smart__capture__open__help"]',
				helpContainer: '[data-jcs-element="capture__help__container"]',
				modalClose: '[data-jcs-element="smart__capture__modal__close__help"]',
				modalGlareContainer: '[data-jcs-element="modal__glare__animation__container"]',
				modalLowResContainer: '[data-jcs-element="modal__low__res__animation__container"]',
				modalBlurContainer: '[data-jcs-element="modal__blur__animation__container"]',
				cameraConditionsContainer: '[data-jcs-element="camera__conditions"]',
				conditionDetailContainer: '[data-jcs-element="camera__condition__detail"]',
				attemptCountContainer: '[data-jcs-element="attempt__count__container"]',
				attemptCounter: '[data-jcs-element="attempt__counter"]',
				cameraCaptureHint: '[data-jcs-element="camera__capture__hint"]',
			}))
			.then(({ cameraStatus, cameraCapture, cameraViewfinder, cameraViewfinderInnerContainer, cameraSelect, errorImg, alignment, blur, uploading, glare, lowResolution, actionContainer, actionText, hintAnimationContainer, cameraOverlay, loadingAnimationContainer, verifyingAnimation, manualCaptureButton, modalManualCaptureButton, modalManualCaptureText, manualCaptureCheck, modalManualCaptureCheck, closeHelp, openHelp, helpContainer, modalClose, modalGlareContainer, modalLowResContainer, modalBlurContainer, cameraConditionsContainer, conditionDetailContainer, attemptCountContainer, attemptCounter, cameraCaptureHint }) => {
				RenderingService.hideObject(cameraSelect);
				RenderingService.hideObject(errorImg);
				RenderingService.hideObject(alignment);
				RenderingService.hideObject(blur);
				RenderingService.hideObject(uploading);
				RenderingService.hideObject(glare);
				RenderingService.hideObject(lowResolution);
				RenderingService.hideObject(actionContainer);
				RenderingService.hideObject(hintAnimationContainer);
				RenderingService.hideObject(manualCaptureButton);
				RenderingService.hideObject(helpContainer);
				RenderingService.hideObject(closeHelp);
				RenderingService.hideObject(modalManualCaptureButton);
				RenderingService.hideObject(modalManualCaptureText);
				RenderingService.hideObject(cameraConditionsContainer);
				RenderingService.hideObject(loadingAnimationContainer);
				RenderingService.hideObject(verifyingAnimation);
				if (this._config.smartCapture?.autoStart)
					RenderingService.hideObject(cameraCapture);

				SmartCaptureProvider.loadingAnimation = AnimationService.CreateAnimation(loadingAnimationContainer as HTMLDivElement, this.assetsFolder + '/animations/loading.json', true, true, {});

				RenderingService.setEnabled(cameraCapture as HTMLInputElement, false);
				RenderingService.setEnabled(cameraSelect as HTMLSelectElement, false);

				if (this._journey) {
					if (this._journey.actionAttempt > 1 &&
						this._config.tripleScanGuidancePage && ((this._journey.action === FlowState.IDENTITY_BACK) || (this._journey.action === FlowState.IDENTITY_FRONT)) && !isFromTripleScanGuidancePage) {

						TripleScanGuidanceProvider.render.call(this, InputProvider.SMART_CAPTURE);
					} else {
						isFromTripleScanGuidancePage = false;
						switch (this._journey.actionAttempt) {
							case 2:
								RenderingService.setText(attemptCounter, '2');
								break;
							case 3:
								RenderingService.setText(attemptCounter, '1');
								break;
							default:
								RenderingService.hideObject(attemptCountContainer);
								break;
						}

						if (this._journey.action === FlowState.IDENTITY_FRONT) {
							RenderingService.setText(actionText, LanguageService.getTranslation(TranslationKey.CAMERA_CAPTURE_FRONT));
						}
						if (this._journey.action === FlowState.IDENTITY_BACK) {
							RenderingService.setText(actionText, LanguageService.getTranslation(TranslationKey.CAMERA_CAPTURE_BACK));
						}
					}
				}

				if (manualCaptureCheck instanceof HTMLInputElement) {
					manualCaptureCheck.addEventListener('change', async () => {
						SmartCaptureProvider.smartCaptureCancel(SmartCaptureFailingReasons.ManualInputProviderChange);
						this._inputProvider = manualCaptureCheck.checked ? InputProvider.CAMERA : InputProvider.SMART_CAPTURE;
						await CameraService.stopStreaming();
						await this.evaluate(true, isPredefinedJourney, this._inputProvider);
					});
				}

				if (modalManualCaptureCheck instanceof HTMLInputElement) {
					modalManualCaptureCheck.addEventListener('change', async () => {
						SmartCaptureProvider.smartCaptureCancel(SmartCaptureFailingReasons.ManualInputProviderChange);
						this._inputProvider = modalManualCaptureCheck.checked ? InputProvider.CAMERA : InputProvider.SMART_CAPTURE;
						await CameraService.stopStreaming();
						await this.evaluate(true, isPredefinedJourney, this._inputProvider);
					});
				}

				if (openHelp instanceof HTMLElement) {
					openHelp.addEventListener('click', () => {
						RenderingService.showObject(helpContainer);
						RenderingService.showObject(closeHelp);
						RenderingService.hideObject(openHelp);
						SmartCaptureProvider.glareAnimation = AnimationService.CreateAnimation(modalGlareContainer as HTMLElement, this.assetsFolder + '/animations/glare.json', true, true, {});
						SmartCaptureProvider.blurAnimation = AnimationService.CreateAnimation(modalBlurContainer as HTMLElement, this.assetsFolder + '/animations/blur.json', true, true, {});
						SmartCaptureProvider.lowResAnimation = AnimationService.CreateAnimation(modalLowResContainer as HTMLElement, this.assetsFolder + '/animations/low-res.json', true, true, {});
						SmartCaptureProvider.glareAnimation.show();
						SmartCaptureProvider.glareAnimation.play();
						SmartCaptureProvider.blurAnimation.show();
						SmartCaptureProvider.blurAnimation.play();
						SmartCaptureProvider.lowResAnimation.show();
						SmartCaptureProvider.lowResAnimation.play();
					});
				}

				if (closeHelp instanceof HTMLElement) {
					closeHelp.addEventListener('click', () => {
						SmartCaptureProvider.closeModal(helpContainer, closeHelp, openHelp);
					});
				}

				if (modalClose instanceof HTMLElement) {
					modalClose.addEventListener('click', () => {
						SmartCaptureProvider.closeModal(helpContainer, closeHelp, openHelp);
					});
				}

				return CameraService.getCameraPermission()
					.then((permission) => {
						switch (permission) {
							case CameraPermissionState.UNKNOWN: // safari 15.5 workaround
							case CameraPermissionState.GRANTED:
								RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.CAMERA_LOADING));
								break;

							default:
								break;
						}
					})
					.then(() => {
						if (cameraSelect instanceof HTMLSelectElement) {
							cameraSelect.addEventListener('change', () => {
								RenderingService.hideObject(manualCaptureButton);
								RenderingService.hideObject(modalManualCaptureButton);
								RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.CAMERA_LOADING));

								RenderingService.setEnabled(cameraSelect, false);

								RenderingService.setEnabled(cameraCapture as HTMLInputElement, false);
								if (SmartCaptureProvider.isRunning && SmartCaptureProvider.smartCaptureCancel != null) {
									SmartCaptureProvider.smartCaptureCancel(SmartCaptureFailingReasons.CameraChanged);
								}

								this.changeCameraStream(cameraSelect.value, ReticleType.DOCUMENT, !this._config.hideLogo, !this._config.hideAutoCaptureHints && SmartCaptureProvider.isRunning ? HintType.AUTOCAPTUREON : HintType.NONE, undefined)
									.then(() => this.renderAvailableCameras(cameraSelect as HTMLSelectElement))
									.then(() => {
										if (SmartCaptureProvider.isRunning && cameraCapture && cameraViewfinder instanceof HTMLCanvasElement) {
											SmartCaptureProvider.startCapturing.call(this,
												hintAnimationContainer,
												cameraSelect,
												errorImg,
												cameraViewfinder,
												cameraViewfinderInnerContainer,
												actionContainer,
												recaptureDelay,
												uploading,
												alignment,
												blur,
												glare,
												lowResolution,
												cameraOverlay,
												manualCaptureButton,
												modalManualCaptureButton,
												modalManualCaptureText,
												cameraCapture,
												cameraCaptureHint,
												conditionDetailContainer,
												cameraConditionsContainer,
												loadingAnimationContainer as HTMLDivElement,
												verifyingAnimation
											);
										}
										RenderingService.hideObject(cameraStatus);
										RenderingService.hideObject(verifyingAnimation);
										RenderingService.setEnabled(cameraSelect, true);
										RenderingService.setEnabled(cameraCapture as HTMLInputElement, true);
									})
									.catch((error) => {
										RenderingService.setEnabled(cameraSelect, true);
										RenderingService.setText(cameraStatus, LanguageService.getTranslation(error.message as TranslationKey));
									});
							});

							RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.SMART_CAPTURE_INITIALIZING));
							if (hintAnimationContainer instanceof HTMLDivElement && cameraViewfinder instanceof HTMLCanvasElement) {
								EventService.subscribe(
									JourneyEvent.CAMERA_CANVAS_RESIZE,
									({ isPortrait }: { isPortrait: boolean }) => {
										SmartCaptureProvider.handleCameraSelectedChange.call(this, hintAnimationContainer, cameraViewfinder, isPortrait);
									}
								);
							} else {
								SmartCaptureProvider.animationCompleted = true;
							}

							return Promise.all([
								IdesMicroHost.createWorker(workerScriptUrl, asmScriptUrl, timeout, timeLogsEnabled),
								this.renderAvailableCameras(cameraSelect as HTMLSelectElement)
							])
								.then(() => {
									if (cameraCapture && cameraViewfinder instanceof HTMLCanvasElement) {
										const startCapturing = (cameraFacingMode) => {
											SmartCaptureProvider.isRunning = true;
											RenderingService.hideObject(cameraOverlay);
											RenderingService.hideObject(verifyingAnimation);
											RenderingService.hideObject(cameraCaptureHint);
											RenderingService.setEnabled(cameraCapture as HTMLSelectElement, false);
											RenderingService.hideObject(cameraCapture as HTMLSelectElement);
											RenderingService.showObject(actionContainer);
											this.changeCameraStream(cameraSelect.value, ReticleType.DOCUMENT, !this._config.hideLogo, !this._config.hideAutoCaptureHints ? HintType.AUTOCAPTUREON : HintType.NONE, undefined, cameraFacingMode)
												.then(() => {
													SmartCaptureProvider.startCapturing.call(this,
														hintAnimationContainer,
														cameraSelect,
														errorImg,
														cameraViewfinder,
														cameraViewfinderInnerContainer,
														actionContainer,
														recaptureDelay,
														uploading,
														alignment,
														blur,
														glare,
														lowResolution,
														cameraOverlay,
														manualCaptureButton,
														modalManualCaptureButton,
														modalManualCaptureText,
														cameraCapture,
														cameraCaptureHint,
														conditionDetailContainer,
														cameraConditionsContainer,
														loadingAnimationContainer as HTMLDivElement,
														verifyingAnimation);
												});
										};

										const startCapturingWithDeviceDecision = async () => {
											let cameraFacingMode = CameraFacingMode.BACK;

											// Ensure this._journey is defined and handle SELFIE step
											if (this._journey?.action === FlowState.SELFIE) {
												cameraFacingMode = CameraFacingMode.FRONT;
											}

											if (cameraCapture && cameraViewfinder instanceof HTMLCanvasElement) {
												// If not in SELFIE step, try to select "Back Dual Wide Camera" as default
												if (cameraSelect instanceof HTMLSelectElement) {
													try {
														const cameras = await CameraService.getVideoCameras();

														let selectedCamera: { deviceId: string, label: string } | undefined = undefined;

														// If in SELFIE mode, find the first camera that is not a back camera
														if (cameraFacingMode === CameraFacingMode.FRONT) {
															selectedCamera = cameras.find(camera => !CameraService.isBackCameraLabel(camera.label));
														} else {
															// For non-SELFIE mode, select "Back Dual Wide Camera" as default for iPhone 13 and above
															selectedCamera = cameras.find(camera => CameraService.isBackDualWideCameraLabel(camera.label));
														}

														const selectedCameraId = selectedCamera ? selectedCamera.deviceId : "";

														// If a camera was found, update the facing mode and mark it as selected
														if (selectedCameraId !== "") {
															cameraFacingMode = cameraFacingMode === CameraFacingMode.FRONT
																? CameraFacingMode.FRONT
																: CameraFacingMode.ENVIRONMENT;

															RenderingService.markSelected(cameraSelect, selectedCameraId);
														}
													} catch (error) {
														// Handle error if CameraService.getVideoCameras() fails
														console.error("Error getting video cameras:", error);
													}
												}
											}

											startCapturing(cameraFacingMode);
										};

										// Check the autoStart flag to decide when to start capturing
										if (this._config.smartCapture?.autoStart) {
											startCapturingWithDeviceDecision();
										}
										cameraCapture.addEventListener('click', startCapturingWithDeviceDecision);

										RenderingService.hideObject(cameraStatus);
										RenderingService.setEnabled(cameraCapture as HTMLSelectElement, true);
										RenderingService.setEnabled(cameraSelect as HTMLSelectElement, true);
									}
								})

								.catch((error) => {
									this._providers = this._providers.filter((provider) => provider !== InputProvider.SMART_CAPTURE);
									this._inputProvider = InputProvider.CAMERA;
									return CameraService.stopStreaming()
										.then(() => IdesMicroHost.destroyWorker())
										.then(() => wait(250))
										.then(() => this.evaluate());
								});

						}
					});

			})
			.then(() => EventService.publish(JourneyEvent.DISPLAY_PROVIDER, { provider: Templates.SmartCapture }));
	}


	private static counterImage: IImageCounter = { Blur: 0, Glare: 0, Resolution: 0, Alignment: 0 };
	private static metaData: IMetaData[] = [];
	private static lengthStartTime: number;
	private static lastStartFrameTime: number;
	private static captureAnimation: lottie.AnimationItem | undefined;
	private static hintAnimation: lottie.AnimationItem | undefined;
	private static captureAnimationComplete: (() => void) | undefined;
	private static glareAnimation: lottie.AnimationItem;
	private static lowResAnimation: lottie.AnimationItem;
	private static blurAnimation: lottie.AnimationItem;
	private static smartCaptureCancel: (reason?: any) => void;
	private static isRunning: boolean;
	private static animationCompleted: boolean;
	private static lastQualityCheckPriority: number;
	private static qualityChecksTimeout: ReturnType<typeof setTimeout>;
	private static loadingAnimation: lottie.AnimationItem | undefined;

	private static startCapturing(
		this: JourneyContainer,
		hintAnimationContainer: HTMLElement | undefined,
		cameraSelect: HTMLSelectElement,
		errorImg: HTMLElement | undefined,
		cameraViewfinder: HTMLCanvasElement,
		cameraViewfinderInnerContainer: HTMLElement | undefined,
		actionContainer: HTMLElement | undefined,
		recaptureDelay: number,
		uploading: HTMLElement | undefined,
		alignment: HTMLElement | undefined,
		blur: HTMLElement | undefined,
		glare: HTMLElement | undefined,
		lowResolution: HTMLElement | undefined,
		cameraOverlay: HTMLElement | undefined,
		manualCaptureButton: HTMLElement | undefined,
		modalManualCaptureButton: HTMLElement | undefined,
		modalManualCaptureText: HTMLElement | undefined,
		cameraCapture: HTMLElement,
		cameraCaptureHint: HTMLElement | undefined,
		conditionDetailContainer: HTMLElement | undefined,
		cameraConditionsContainer: HTMLElement | undefined,
		loadingAnimationContainer: HTMLDivElement | undefined,
		verifyingAnimation: HTMLElement | undefined) {
		SmartCaptureProvider.lastQualityCheckPriority = -1;
		RenderingService.showObject(hintAnimationContainer);
		this.renderAvailableCameras(cameraSelect as HTMLSelectElement);
		EventService.publish(JourneyEvent.CAMERA_START);
		SmartCaptureProvider.lengthStartTime = performance.now();
		const expiryDate = new Date();
		expiryDate.setMilliseconds(expiryDate.getMilliseconds() + 60000);
		const { cancel, token } = CancellationToken.create();
		SmartCaptureProvider.smartCaptureCancel = cancel;

		if (this._manualCapture.enabled) {
			if (this._manualCapture.timeout <= 60 && this._manualCapture.timeout >= 0) {
				const smartCaptureTimeout = this._manualCapture.timeout * 1000;

				setTimeout(() => {
					if (this._manualCapture.enabled) {
						RenderingService.showObject(manualCaptureButton);
						RenderingService.showObject(modalManualCaptureButton);
						RenderingService.showObject(modalManualCaptureText);
					}
				}, smartCaptureTimeout);
			} else {
				this._manualCapture.enabled = false;
			}
		}
		if (ENV === 'DEVELOPMENT') {
			console.log(`Start smartcapture to expire at ${expiryDate}`);
		}

		retryOperationWithTimeout(() => SmartCaptureProvider.processFrame.call(this, errorImg as HTMLElement, cameraViewfinder, cameraViewfinderInnerContainer, hintAnimationContainer, actionContainer, verifyingAnimation), recaptureDelay, expiryDate, token)
			.then(({ image, result }) => ImageService.convert(image)
				.then(async (image64) => {
					RenderingService.showObject(loadingAnimationContainer);
					if (this._previewPages.documentStep.smartCapture) {
						RenderingService.setText(verifyingAnimation, LanguageService.getTranslation(TranslationKey.PREPARING_PREVIEW_TITLE));
					} else {

						RenderingService.setText(verifyingAnimation, LanguageService.getTranslation(TranslationKey.VERIFYING_TITLE));
					}
					RenderingService.showObject(verifyingAnimation);
					SmartCaptureProvider.isRunning = false;
					SmartCaptureProvider.setMetaData();
					await EventService.publish(JourneyEvent.CAMERA_FEEDBACK, { succeeded: true });
					// Stop the camera streaming
					await CameraService.stopStreaming();
					CameraService.clearCanvas(cameraViewfinder);
					RenderingService.showObject(cameraOverlay);
					RenderingService.removeClass(cameraViewfinderInnerContainer, 'camera-viewfinder--inner-container-error');
					RenderingService.addClass(cameraViewfinderInnerContainer, `camera-viewfinder--inner-container-success`);
					SmartCaptureProvider.removeIDAnimation();
					// show uploading object
					// Hide smart capture objects
					RenderingService.hideObject(errorImg);
					RenderingService.hideObject(alignment);
					RenderingService.hideObject(blur);
					RenderingService.hideObject(glare);
					RenderingService.hideObject(lowResolution);
					RenderingService.hideObject(conditionDetailContainer);
					setTimeout(() => {
						if (this._previewPages.documentStep.smartCapture) {
							return ImageProvider.render.call(this, image64, SmartCaptureProvider.metaData);
						} else {
							return SmartCaptureProvider.continueJourneySkipPreview.call(this, image64, SmartCaptureProvider.metaData);
						}
					}, 1000);
				})
			)
			.catch((reason?: any) => {
				if (ENV === 'DEVELOPMENT') {
					console.log(`Start smartcapture cancelled`, reason);
				}
				if (reason === SmartCaptureFailingReasons.ManualInputProviderChange) {
					return;
				}
				RenderingService.removeClass(cameraViewfinderInnerContainer, 'camera-viewfinder--inner-container-error');
				RenderingService.hideObject(errorImg);
				RenderingService.hideObject(alignment);
				RenderingService.hideObject(blur);
				RenderingService.hideObject(glare);
				RenderingService.hideObject(lowResolution);
				RenderingService.hideObject(actionContainer);
				RenderingService.hideObject(uploading);
				RenderingService.hideObject(cameraConditionsContainer);
				SmartCaptureProvider.removeIDAnimation();
				if (reason === SmartCaptureFailingReasons.CameraChanged) {
					return;
				}
				RenderingService.showObject(cameraCaptureHint);
				RenderingService.showObject(cameraCapture as HTMLSelectElement);
				RenderingService.setEnabled(cameraCapture as HTMLSelectElement, true);
				RenderingService.setEnabled(cameraSelect as HTMLSelectElement, true);
				RenderingService.showObject(cameraOverlay);
				this.changeCameraStream(cameraSelect.value, ReticleType.DOCUMENT, !this._config.hideLogo, HintType.NONE, undefined, CameraFacingMode.BACK);
				SmartCaptureProvider.isRunning = false;
				EventService.publish(JourneyEvent.CAMERA_FEEDBACK, { failed: true });
			});
	}

	private static continueJourneySkipPreview(this: JourneyContainer, image: string, metaData: IMetaData[]) {
		const { type } = ImageService.getBase64Components(image);
		return this.assertImageExists(image)
			.then(() => this.getJourney())
			.then((journey) => journey.continue({
				imgList: [{
					...ImageService.remapNames(ImageService.getBase64Components(image)),
					name: journey.action === 'SELFIE' ? RequiredInputImage.SelfiePhoto : RequiredInputImage.WhiteImage,
					inputFormat: type === 'pdf' ? 1 : 0
				}],
				journeyDefinitionId: this._journeyDefinitionId
			}, metaData)
				.then((res) => DataService.getDataStore<IIdScanData>(DataStoreType.ID_SCAN).update(ImageProvider.parseResponseData(res)))
				.then(() => this.evaluate())
				.then(this.updateJourneyFields));
	}

	private static removeIDAnimation() {
		if (SmartCaptureProvider.captureAnimationComplete) {
			SmartCaptureProvider.captureAnimationComplete();
			SmartCaptureProvider.captureAnimationComplete = undefined;
		}

		if (SmartCaptureProvider.captureAnimation) {
			SmartCaptureProvider.captureAnimation.removeEventListener('complete');
			SmartCaptureProvider.captureAnimation.stop();
			SmartCaptureProvider.captureAnimation.destroy();
			SmartCaptureProvider.captureAnimation = undefined;
		}
	}

	private static closeModal(helpContainer: HTMLElement | undefined, closeHelp: HTMLElement | undefined, openHelp: HTMLElement | undefined) {
		RenderingService.hideObject(helpContainer);
		RenderingService.hideObject(closeHelp);
		RenderingService.showObject(openHelp);
		SmartCaptureProvider.glareAnimation.stop();
		SmartCaptureProvider.glareAnimation.destroy();
		SmartCaptureProvider.blurAnimation.stop();
		SmartCaptureProvider.blurAnimation.destroy();
		SmartCaptureProvider.lowResAnimation.stop();
		SmartCaptureProvider.lowResAnimation.destroy();
	}

	private static setMetaData = () => {
		const lengthStart = performance.now() - SmartCaptureProvider.lengthStartTime;
		const lastFrameTime = performance.now() - SmartCaptureProvider.lastStartFrameTime;
		const numberOfFrames = SmartCaptureProvider.counterImage.Blur + SmartCaptureProvider.counterImage.Glare + SmartCaptureProvider.counterImage.Resolution;

		SmartCaptureProvider.metaData = [{
			name: 'Capture Type', // Smart Capture or Manual Capture
			value: 'Smart Capture',
			step: '',
			type: 'Simple'
		}, {
			name: 'SC: Length of Smart Capture', // The time period from start to end of smart capture
			value: lengthStart.toString(),
			step: '',
			type: 'Simple'
		}, {
			name: 'SC: Number of Frames', // Smart Capture or Manual Capture
			value: numberOfFrames.toString(),
			step: '',
			type: 'Simple'
		}, {
			name: 'SC: Last Processes Frame', // The last processed frames time
			value: lastFrameTime.toString(),
			step: '',
			type: 'Simple'
		}, {
			name: 'SC: Number of Glary frames', // Number of frames that had glare
			value: SmartCaptureProvider.counterImage.Glare.toString(),
			step: '',
			type: 'Simple'
		}, {
			name: 'SC: Number of Blurry frames', // Number of frames that had blure
			value: SmartCaptureProvider.counterImage.Blur.toString(),
			step: '',
			type: 'Simple'
		}, {
			name: 'SC: Number of Low-Resolution frames', // Number of low res frames
			value: SmartCaptureProvider.counterImage.Resolution.toString(),
			step: '',
			type: 'Simple'
		}];
	}

	private static setConditionsFlags(this: JourneyContainer, conditions: IImageResult['quality']) {
		SmartCaptureProvider.setConditionChecks.call(this, conditions.blurCheck, conditions.glareCheck, conditions.resolutionCheck, conditions.coordinateCheck);
	}

	private static setConditionChecks(this: JourneyContainer, blurCheck: boolean, glareCheck: boolean, resolutionCheck: boolean, alignmentCheck: boolean) {
		return TemplateService.getElements(this._container, {
			blur: '[data-jcs-element="camera__condition__blur"]',
			glare: '[data-jcs-element="camera__condition__glare"]',
			lowResolution: '[data-jcs-element="camera__condition__low__resolution"]',
			alignment: '[data-jcs-element="camera__condition__alignment"]',
			cameraConditionsContainer: '[data-jcs-element="camera__conditions"]'
		})
			.then(({ blur, glare, lowResolution, alignment, cameraConditionsContainer }) => {
				if (ENV === 'DEVELOPMENT') {
					console.log(`**** QUALITY CHECKS **** GLARE: ${glareCheck}; BLUR: ${blurCheck}; RESOLUTION: ${resolutionCheck}; ALIGNEMENT: ${alignmentCheck} **** END QUALITY CHECKS ****`);
				}
				RenderingService[glareCheck ? 'hideObject' : 'showObject'](glare);
				RenderingService[blurCheck ? 'hideObject' : 'showObject'](blur);
				RenderingService[(resolutionCheck) ? 'hideObject' : 'showObject'](lowResolution);
				RenderingService[(alignmentCheck) ? 'hideObject' : 'showObject'](alignment);
				RenderingService[(alignmentCheck && blurCheck && glareCheck && resolutionCheck) ? 'hideObject' : 'showObject'](cameraConditionsContainer);

				SmartCaptureProvider.setConditionChecksDetail.call(this, glareCheck, blurCheck, resolutionCheck, alignmentCheck);

			});
	}

	private static setConditionChecksDetail(this: JourneyContainer, glareCheck: boolean, blurCheck: boolean, resolutionCheck: boolean, alignmentCheck: boolean) {
		return TemplateService.getElements(this._container, {
			detailContainer: '[data-jcs-element="camera__condition__detail"]'
		})
			.then(({ detailContainer }) => {
				// tslint:disable-next-line:no-bitwise
				const checkPriority = ((alignmentCheck ? 0 : 1) << 3)
					// tslint:disable-next-line:no-bitwise
					| ((resolutionCheck ? 0 : 1) << 2)
					// tslint:disable-next-line:no-bitwise
					| ((blurCheck ? 0 : 1) << 1)
					| (glareCheck ? 0 : 1);
				if (checkPriority > SmartCaptureProvider.lastQualityCheckPriority) {
					SmartCaptureProvider.lastQualityCheckPriority = checkPriority;
					if (!alignmentCheck) {
						SmartCaptureProvider.showTitleHintAndAnimation.call(
							this,
							LanguageService.getTranslation(TranslationKey.CAMERA_CONDITION_ALIGNMENT),
							LanguageService.getTranslation(TranslationKey.CAMERA_PREVENT_LOW_RES_HINT),
							this.assetsFolder + '/animations/low-res-white.json');
					} else if (!resolutionCheck) {
						SmartCaptureProvider.showTitleHintAndAnimation.call(
							this,
							LanguageService.getTranslation(TranslationKey.CAMERA_CONDITION_LOW_RESOLUTION),
							LanguageService.getTranslation(TranslationKey.CAMERA_PREVENT_LOW_RES_HINT),
							this.assetsFolder + '/animations/low-res-white.json');
					} else if (!blurCheck) {
						SmartCaptureProvider.showTitleHintAndAnimation.call(
							this,
							LanguageService.getTranslation(TranslationKey.CAMERA_CONDITION_BLUR),
							LanguageService.getTranslation(TranslationKey.CAMERA_PREVENT_BLUR_HINT),
							this.assetsFolder + '/animations/blur-white.json');
					} else if (!glareCheck) {
						SmartCaptureProvider.showTitleHintAndAnimation.call(
							this,
							LanguageService.getTranslation(TranslationKey.CAMERA_CONDITION_GLARE),
							LanguageService.getTranslation(TranslationKey.CAMERA_PREVENT_GLARE_HINT),
							this.assetsFolder + '/animations/glare-white.json');
					} else {
						RenderingService.hideObject(detailContainer);
					}
				} else if (checkPriority === SmartCaptureProvider.lastQualityCheckPriority && checkPriority) {
					if (SmartCaptureProvider.qualityChecksTimeout) {
						clearTimeout(SmartCaptureProvider.qualityChecksTimeout);
					}
					SmartCaptureProvider.qualityChecksTimeout = setTimeout(() => {
						SmartCaptureProvider.lastQualityCheckPriority = -1;
					}, 4000);
				}
			});
	}

	private static showTitleHintAndAnimation(this: JourneyContainer, titleText: string, hintText: string, animationPath: string) {
		return TemplateService.getElements(this._container, {
			detailContainer: '[data-jcs-element="camera__condition__detail"]',
			animation: '[data-jcs-element="camera__condition__detail__animation"]',
			title: '[data-jcs-element="camera__condition__detail__description__title"]',
			hint: '[data-jcs-element="camera__condition__detail__description__hint"]',
		})
			.then(({ detailContainer, animation, title, hint }) => {
				RenderingService.showObject(detailContainer);
				RenderingService.setText(title, titleText);
				RenderingService.setText(hint, hintText);
				if (animation instanceof HTMLDivElement) {
					SmartCaptureProvider.RemoveHintImage();
					SmartCaptureProvider.hintAnimation = AnimationService.CreateAnimation(
						animation,
						animationPath,
						true,
						true,
						{});
				}
			});
	}

	private static RemoveHintImage() {
		if (SmartCaptureProvider.hintAnimation !== undefined) {
			SmartCaptureProvider.hintAnimation.stop();
			SmartCaptureProvider.hintAnimation.destroy();
			SmartCaptureProvider.hintAnimation = undefined;
		}
	}

	private static processFrame(this: JourneyContainer, errorImg: HTMLElement, cameraViewfinder: HTMLCanvasElement, cameraViewfinderInnerContainer: HTMLElement | undefined, hintAnimationContainer: HTMLElement | undefined, verifyingAnimation: HTMLElement | undefined, actionContainer: HTMLElement | undefined): Promise<{ image: Blob, result: IImageResult }> {
		SmartCaptureProvider.lastStartFrameTime = performance.now();
		return new Promise((resolve, reject) => {
			var startTime = performance.now();

			if (CameraService.hasActiveStream()) {
				var blobFromCurrentStreamStartTime = performance.now();

				var blobFromCurrentStream = CameraService.getBlobFromCurrentStream()
					.then((image) => {
						var result = IdesMicroHost.checkImage(image);

						var blobFromCurrentStreamEndTime = performance.now();
						if (this._config.isVerboseLogEnabled)
							console.log(`| getBlobFromCurrentStream method took: ${blobFromCurrentStreamEndTime - blobFromCurrentStreamStartTime} ms |`);

						return result;
					})
					.then((result) => {
						var renderingStartTime = performance.now();

						if (SmartCaptureProvider.animationCompleted) {
							SmartCaptureProvider.setConditionsFlags.call(this, { ...result.quality });
							const hasChecks = result.quality.blurCheck || result.quality.glareCheck || result.quality.resolutionCheck;
							RenderingService[hasChecks ? 'showObject' : 'hideObject'](errorImg);
							RenderingService.addClass(cameraViewfinderInnerContainer,
								`camera-viewfinder--inner-container-${hasChecks ? 'error' : 'success'}`);
							RenderingService.hideObject(hintAnimationContainer);
							RenderingService.hideObject(actionContainer);
							if (SmartCaptureProvider.captureAnimation) {
								SmartCaptureProvider.captureAnimation.hide();
							}
						} else if (SmartCaptureProvider.lastQualityCheckPriority === 0) {
							RenderingService.removeClass(cameraViewfinderInnerContainer, 'camera-viewfinder--inner-container-error');
							RenderingService.hideObject(errorImg);
							RenderingService.showObject(verifyingAnimation);
							RenderingService.showObject(hintAnimationContainer);
							RenderingService.showObject(actionContainer);
							SmartCaptureProvider.RemoveHintImage();
							SmartCaptureProvider.setConditionChecks.call(this, true, true, true, true);
							if (SmartCaptureProvider.captureAnimation) {
								SmartCaptureProvider.captureAnimation.show();
								SmartCaptureProvider.captureAnimation.play();
							}
						}

						if (result && result.quality) {
							if (result.quality.blurCheck) { SmartCaptureProvider.counterImage.Blur++; }
							if (result.quality.glareCheck) { SmartCaptureProvider.counterImage.Glare++; }
							if (result.quality.resolutionCheck) { SmartCaptureProvider.counterImage.Resolution++; }

							if (result.image == undefined) {
								return EventService.publish(JourneyEvent.CAMERA_FEEDBACK, { ...result.quality })
									.then(() => reject({ result }));
							} else {
								return EventService.publish(JourneyEvent.CAMERA_FEEDBACK, { ...result.quality })
									.then(() => resolve({ image: new Blob([result.image], { type: 'image/png' }), result: result }));
							}
						}

						var renderingEndTime = performance.now();
						console.log(`| rendering took: ${renderingEndTime - renderingStartTime} ms |`);
					});


				var endTime = performance.now();

				if (this._config.isVerboseLogEnabled)
					console.log(`| processFrame method took: ${endTime - startTime} ms |`);

				return blobFromCurrentStream;
			} else {
				return reject(false);
			}
		});
	}

	private static handleCameraSelectedChange(this: JourneyContainer, animationContainer: HTMLDivElement, canvasContainer: HTMLCanvasElement, isPortrait: boolean) {
		try {
			if (SmartCaptureProvider.isRunning) {

				type LottiePlayer = typeof lottie.default;
				const lottiePlayer = lottie as any as LottiePlayer;
				animationContainer.style.height = `${canvasContainer.clientHeight}px`;
				animationContainer.style.width = `${canvasContainer.clientWidth}px`;
				let imagePath = '';

				if (!isPortrait) {
					if (this._journey && this._journey.action === FlowState.IDENTITY_FRONT) {
						imagePath = this.assetsFolder + '/animations/position-document-v5.json';
					} else if (this._journey && this._journey.action === FlowState.IDENTITY_BACK) {
						imagePath = this.assetsFolder + '/animations/flip-document-v2.json';
					}
				} else {
					if (this._journey && this._journey.action === FlowState.IDENTITY_FRONT) {
						imagePath = this.assetsFolder + '/animations/rotate-document-v3-1.json';
					} else if (this._journey && this._journey.action === FlowState.IDENTITY_BACK) {
						imagePath = this.assetsFolder + '/animations/flip-document-mobile-v2.json';
					}
				}
				SmartCaptureProvider.removeIDAnimation();

				SmartCaptureProvider.animationCompleted = false;
				SmartCaptureProvider.captureAnimation = AnimationService.CreateAnimation(
					animationContainer,
					imagePath,
					false,
					true,
					{});

				SmartCaptureProvider.captureAnimationComplete = SmartCaptureProvider.captureAnimation.addEventListener('complete', (_) => {
					SmartCaptureProvider.animationCompleted = true;
					SmartCaptureProvider.removeIDAnimation();
				});
			}
		} catch (error) {
			console.log(error);
		}
	}
}
